import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Image,
  Loader,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { AppIcon, SearchInput } from "@/components/generic";
import {
  UnsplashCollection,
  UnsplashCollectionBlockSettings as UnsplashCollectionBlockSettingsData,
  UnsplashImage,
} from "@/types/unsplashTypes";
import { UnsplashService } from "@/utils/api/UnsplashService";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface UnsplashCollectionBlockSettingsProps {
  value: UnsplashCollectionBlockSettingsData | null;
  onChange: (settings: UnsplashCollectionBlockSettingsData) => void;
  errors?: Partial<Record<keyof UnsplashCollectionBlockSettingsData, string>>;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: UnsplashCollectionBlockSettingsData = {
  collectionId: "",
  collectionTitle: "",
  perPage: 30,
  mode: "cycle_all",
};

// ─── Component ────────────────────────────────────────────────────────────────

export const UnsplashCollectionBlockSettings: React.FC<
  UnsplashCollectionBlockSettingsProps
> = ({ value, onChange, errors = {} }) => {
  const settings = value ?? DEFAULT_SETTINGS;

  // ── Search state ─────────────────────────────────────────────────────────

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UnsplashCollection[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selected collection preview
  const [selectedCollection, setSelectedCollection] =
    useState<UnsplashCollection | null>(null);
  const [previewImages, setPreviewImages] = useState<UnsplashImage[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [hasMorePreview, setHasMorePreview] = useState(true);

  // Abort controller for in-flight requests
  const abortRef = useRef<AbortController | null>(null);

  // ── Search ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const run = async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const results = await UnsplashService.search(searchQuery, 1, 10);
        setSearchResults(results);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setSearchError(err.message);
        }
      } finally {
        setIsSearching(false);
      }
    };

    run();
  }, [searchQuery]);

  // ── Restore selected collection label on edit ─────────────────────────────

  useEffect(() => {
    if (
      settings.collectionId &&
      settings.collectionTitle &&
      !selectedCollection
    ) {
      // Reconstruct a minimal preview from stored data — no extra API call needed
      setSelectedCollection({
        id: settings.collectionId,
        title: settings.collectionTitle,
      } as UnsplashCollection);
    }
  }, [settings.collectionId, settings.collectionTitle, selectedCollection]);

  // ── Fetch preview images for selected collection ─────────────────────────

  useEffect(() => {
    if (!selectedCollection?.id) {
      setPreviewImages([]);
      setPreviewPage(1);
      setHasMorePreview(true);
      return;
    }

    const fetchPreview = async () => {
      setIsLoadingPreview(true);
      try {
        const images = await UnsplashService.getPhotos(
          selectedCollection.id,
          undefined,
          1,
          12,
        );
        setPreviewImages(images);
        setPreviewPage(1);
        setHasMorePreview(images.length === 12);
      } catch (err) {
        console.error("Failed to fetch preview images:", err);
        setPreviewImages([]);
        setHasMorePreview(false);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    fetchPreview();
  }, [selectedCollection?.id]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectCollection = useCallback(
    (collection: UnsplashCollection) => {
      setSelectedCollection(collection);
      setSearchQuery("");
      setSearchResults([]);
      onChange({
        ...settings,
        collectionId: collection.id,
        collectionTitle: collection.title,
      });
    },
    [settings, onChange],
  );

  const handleLoadMorePreview = useCallback(async () => {
    if (!selectedCollection?.id || isLoadingPreview || !hasMorePreview) return;

    setIsLoadingPreview(true);
    try {
      const nextPage = previewPage + 1;
      const newImages = await UnsplashService.getPhotos(
        selectedCollection.id,
        undefined,
        nextPage,
        12,
      );
      setPreviewImages((prev) => [...prev, ...newImages]);
      setPreviewPage(nextPage);
      setHasMorePreview(newImages.length === 12);
    } catch (err) {
      console.error("Failed to load more preview images:", err);
      setHasMorePreview(false);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [selectedCollection?.id, isLoadingPreview, hasMorePreview, previewPage]);

  const handleClearCollection = useCallback(() => {
    setSelectedCollection(null);
    setPreviewImages([]);
    setPreviewPage(1);
    setHasMorePreview(true);
    onChange({
      ...settings,
      collectionId: "",
      collectionTitle: "",
    });
  }, [settings, onChange]);

  const handleModeChange = useCallback(
    (mode: string) => {
      onChange({
        ...settings,
        mode: mode as UnsplashCollectionBlockSettingsData["mode"],
      });
    },
    [settings, onChange],
  );

  const handlePerPageChange = useCallback(
    (perPage: number | string) => {
      onChange({
        ...settings,
        perPage:
          typeof perPage === "number" ? perPage : parseInt(String(perPage), 10),
      });
    },
    [settings, onChange],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const hasCollection = Boolean(settings.collectionId);

  return (
    <Stack gap="md">
      {/* ── Collection Picker ── */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Unsplash Collection
        </Text>

        {hasCollection && selectedCollection ? (
          // Selected collection preview
          <Stack gap="sm">
            <Card withBorder padding="sm" radius="md">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  {selectedCollection.coverPhoto?.urls?.thumb && (
                    <Image
                      src={selectedCollection.coverPhoto.urls.thumb}
                      w={56}
                      h={56}
                      radius="sm"
                      fit="cover"
                      style={{ flexShrink: 0 }}
                    />
                  )}
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} truncate>
                      {selectedCollection.title}
                    </Text>
                    {selectedCollection.totalPhotos != null && (
                      <Text size="xs" c="dimmed">
                        {selectedCollection.totalPhotos.toLocaleString()} photos
                      </Text>
                    )}
                    {selectedCollection.user?.name && (
                      <Text size="xs" c="dimmed">
                        by {selectedCollection.user.name}
                      </Text>
                    )}
                  </Stack>
                </Group>
                <Tooltip label="Change collection">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={handleClearCollection}
                    aria-label="Remove selected collection"
                  >
                    <AppIcon iconName="X" size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>

            {/* Preview images */}
            {isLoadingPreview && previewImages.length === 0 ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Loading preview...
                </Text>
              </Group>
            ) : previewImages.length > 0 ? (
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Preview Images
                </Text>
                <Grid gutter="xs">
                  {previewImages.map((image) => (
                    <Grid.Col key={image.id} span={4}>
                      <Image
                        src={image.urls.thumb}
                        w="100%"
                        h={60}
                        radius="sm"
                        fit="cover"
                        alt={image.altDescription || image.description || ""}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
                {hasMorePreview && (
                  <Group justify="center" mt="sm">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={handleLoadMorePreview}
                      loading={isLoadingPreview}
                    >
                      Load More Images
                    </Button>
                  </Group>
                )}
              </Box>
            ) : null}
          </Stack>
        ) : (
          // Search input
          <Stack gap="xs">
            <SearchInput
              placeholder="Search Unsplash collections..."
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => {
                setSearchQuery("");
                setSearchResults([]);
              }}
              error={errors.collectionId}
              leftSection={isSearching ? <Loader size={14} /> : undefined}
              size="md"
            />

            {searchError && (
              <Alert
                color="red"
                icon={<AppIcon iconName="AlertCircle" size={16} />}
              >
                <Text size="sm">{searchError}</Text>
              </Alert>
            )}

            {/* Search results */}
            {searchResults.length > 0 && (
              <Stack gap={4}>
                {searchResults.map((collection) => (
                  <Card
                    key={collection.id}
                    withBorder
                    padding="xs"
                    radius="md"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelectCollection(collection)}
                  >
                    <Group gap="sm" wrap="nowrap">
                      {collection.coverPhoto?.urls?.thumb && (
                        <Image
                          src={collection.coverPhoto.urls.thumb}
                          w={48}
                          h={48}
                          radius="sm"
                          fit="cover"
                          style={{ flexShrink: 0 }}
                        />
                      )}
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {collection.title}
                        </Text>
                        <Group gap="xs">
                          {collection.totalPhotos != null && (
                            <Text size="xs" c="dimmed">
                              {collection.totalPhotos.toLocaleString()} photos
                            </Text>
                          )}
                          {collection.user?.name && (
                            <Text size="xs" c="dimmed">
                              · {collection.user.name}
                            </Text>
                          )}
                        </Group>
                      </Stack>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}

            {searchQuery &&
              !isSearching &&
              searchResults.length === 0 &&
              !searchError && (
                <Text size="sm" c="dimmed" ta="center" py="xs">
                  No collections found for "{searchQuery}"
                </Text>
              )}
          </Stack>
        )}
      </Stack>

      {/* ── Display Mode ── */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Display Mode
        </Text>
        <SegmentedControl
          value={settings.mode}
          onChange={handleModeChange}
          data={[
            {
              value: "cycle_all",
              label: (
                <Box py={4}>
                  <Text size="sm" fw={500}>
                    Show full collection
                  </Text>
                  <Text size="xs" c="dimmed">
                    Play all images before advancing
                  </Text>
                </Box>
              ),
            },
            {
              value: "one_per_pass",
              label: (
                <Box py={4}>
                  <Text size="sm" fw={500}>
                    One image per loop
                  </Text>
                  <Text size="xs" c="dimmed">
                    Show one image, resume next time
                  </Text>
                </Box>
              ),
            },
          ]}
          fullWidth
        />
      </Stack>

      {/* ── Images per page ── */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="Images per page"
            description="How many images to fetch per API request (10–30)"
            value={settings.perPage ?? 30}
            onChange={handlePerPageChange}
            error={errors.perPage}
            min={10}
            max={30}
            allowNegative={false}
            allowDecimal={false}
            size="md"
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
